import { TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { Token } from '@/domain/types/Token'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId, usePublicClient, useReadContract, useWalletClient } from 'wagmi'
import { formatUnits } from 'viem'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { getFormFieldsForMintForm, useDebouncedMintFormValues } from './form'
import { FormFieldsForDialog, GsmBurnState } from './types'
import { getGSMBurnReserves } from './utils'
import { getMintFormValidator, MintFormSchema } from './validators'
import { assert } from '@/utils/assert'
import { useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { useAccount } from '@/domain/hooks/useAccount'
import { usePrivy } from '@privy-io/react-auth'
import { gsmBurn } from './gsmBurn'
import { gsmConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { toBigInt } from '@/utils/bigNumber'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { formFormat } from '@/domain/common/format'

export interface UseBurnFormOptions {
  initialToken: Token
}
export interface UseGsmBurnFormResult {
  assetsToBurnFields: FormFieldsForDialog
  fromAsset: TokenWithValue
  toAsset: TokenWithValue
  pageStatus: PageStatus
  form: UseFormReturn<MintFormSchema>
  guestMode: boolean
  gsmBurnState: GsmBurnState
  actionsEnabled: boolean
  openConnectModal: () => void
  gsmBurn: () => void
  resetForm: () => void
}

export function useGsmBurnForm(): UseGsmBurnFormResult {
  const chainId = useChainId()

  const { connectWallet } = usePrivy()
  const { marketInfo } = useMarketInfo({ chainId, refetchInterval: 0 })
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const walletInfo = useMarketWalletInfo({ chainId })

  const [gsmBurnState, setGsmBurnState] = useState<GsmBurnState>({
    message: '',
    status: 'idle',
  })

  const account = useAccount()
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()

  const walletConfig = {
    account,
    publicClient,
    walletClient,
  }

  const { fromAssets, toAssets } = getGSMBurnReserves(marketInfo, walletInfo)

  assert(fromAssets[0] && toAssets[0], 'No GSM burn tokens found')

  const form = useForm<MintFormSchema>({
    resolver: zodResolver(getMintFormValidator({ marketInfo, walletInfo }), undefined, { raw: true }),
    defaultValues: {
      fromAsset: {
        symbol: fromAssets[0].token.symbol,
        value: '',
      },
      toAsset: {
        symbol: toAssets[0].token.symbol,
        value: '',
      },
    },
    mode: 'onChange',
  })

  const { debouncedFormValues, isDebouncing, isFormValid } = useDebouncedMintFormValues({
    form,
    marketInfo,
  })

  const fromAsset = useConditionalFreeze(debouncedFormValues.fromAsset, pageStatus === 'success')
  const toAsset = useConditionalFreeze(debouncedFormValues.toAsset, pageStatus === 'success')
  const baseFields = getFormFieldsForMintForm({ form, marketInfo, fromAssets, toAssets, walletInfo })
  const assetsToBurnFields: FormFieldsForDialog = {
    ...baseFields,
    changeFromValue: (e) => {
      form.setValue('fromAsset.value', e.target.value)
      form.setValue('toAsset.value', '')
      form.trigger('fromAsset')
    },
    onSetMax: () => {
      form.setValue('toAsset.value', '')
    },
  }

  const gsmAddress = getContractAddress(gsmConfig.address, chainId)
  const fromValueBigInt = debouncedFormValues.fromAsset.value.gt(0)
    ? toBigInt(debouncedFormValues.fromAsset.token.toBaseUnit(debouncedFormValues.fromAsset.value))
    : 0n

  const { data: burnQuote } = useReadContract({
    address: gsmAddress,
    abi: gsmConfig.abi,
    functionName: 'getAssetAmountForBuyAsset',
    args: [fromValueBigInt],
    query: { enabled: fromValueBigInt > 0n },
  })

  const toAssetToken = toAssets[0].token
  const toAssetDecimals = toAssetToken.decimals
  useEffect(() => {
    if (pageStatus !== 'form') return
    if (fromValueBigInt === 0n) {
      if (form.getValues('toAsset.value') !== '') {
        form.setValue('toAsset.value', '', { shouldValidate: true })
      }
      return
    }
    if (!burnQuote) return
    const assetBought = burnQuote[0]
    const formatted = formFormat(NormalizedUnitNumber(formatUnits(assetBought, toAssetDecimals)), toAssetDecimals)
    if (form.getValues('toAsset.value') !== formatted) {
      form.setValue('toAsset.value', formatted, { shouldValidate: true })
    }
  }, [burnQuote, fromValueBigInt, toAssetDecimals, pageStatus, form])

  const actionsEnabled =
    debouncedFormValues.fromAsset.value.gt(0) &&
    debouncedFormValues.toAsset.value.gt(0) &&
    isFormValid &&
    !isDebouncing &&
    pageStatus === 'form'

  const handleGsmBurn = async () => {
    return gsmBurn({ setGsmBurnState, fromAsset, walletConfig, chainId, onSuccess: () => setPageStatus('success') })
  }

  return {
    form,
    assetsToBurnFields,
    fromAsset,
    toAsset,
    guestMode: !account,
    gsmBurnState,
    actionsEnabled,
    gsmBurn: handleGsmBurn,
    openConnectModal: () => connectWallet(),
    resetForm: () => {
      form.reset()
      setPageStatus('form')
    },
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
  }
}
